import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {styled} from '@mui/material/styles';
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from '@mui/x-data-grid';
import React, {useEffect} from 'react';

import {handleError} from '../../utils/errors';

const StyledBox = styled(Box)(({theme}) => ({
  height: 600,
  width: '100%',
  '& .actions': {
    color: 'text.secondary',
  },
  '& .textPrimary': {
    color: 'text.primary',
  },
  '& .header-bg': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.dark2,
  },
}));

function EditToolbar(props) {
  const {setRows, setRowModesModel, addRecordMetaData} = props;

  const handleClick = () => {
    const id = `${Date.now()}`;
    setRows((oldRows) => [...oldRows, {id, isNew: true, ...addRecordMetaData?.defaultFields}]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: {mode: GridRowModes.Edit, fieldToFocus: addRecordMetaData?.fieldToFocus},
    }));
  };

  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        Add record
      </Button>
    </GridToolbarContainer>
  );
}

export default function CustomEditableTable({
  columns,
  initialRows,
  addRowFn,
  updateRowFn,
  addRecordMetaData,
  setSnackBarAlert,
}) {
  columns = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      headerClassName: 'header-bg',
      getActions: ({id}) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={id}
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={id}
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={id}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
        ];
      },
    },
    ...columns,
  ];
  const [rows, setRows] = React.useState(initialRows || []);
  const [rowModesModel, setRowModesModel] = React.useState({});

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: {mode: GridRowModes.View, ignoreModifications: true},
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = async (newRow, oldRow) => {
    let id = newRow.id;
    if (newRow.isNew === true) {
      id = await addRowFn(newRow);
    } else {
      const dataToSave = {
        id: isFinite(newRow?.id) ? +newRow.id : newRow.id,
      };
      Object.keys(newRow).forEach((key) => {
        // save only if change in old values
        if (newRow[key] !== oldRow[key]) {
          dataToSave[key] = newRow[key];
        }
      });
      if (Object.keys(dataToSave).length > 1) {
        await updateRowFn(dataToSave, oldRow);
      }
    }
    const updatedRow = {...newRow, id, isNew: false};
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    setSnackBarAlert((prevState) => ({
      ...prevState,
      isOpen: true,
      message: `id: ${id} saved`,
      severity: 'success',
    }));
    return updatedRow;
  };

  const onProcessRowUpdateError = (error) => {
    setSnackBarAlert((prevState) => ({
      ...prevState,
      isOpen: true,
      message: handleError(error),
      severity: 'error',
    }));
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  return (
    <>
      <StyledBox>
        <DataGrid
          rows={rows}
          columns={columns}
          editMode="row"
          disableColumnMenu
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onProcessRowUpdateError}
          slots={{
            toolbar: addRowFn ? EditToolbar : null,
          }}
          slotProps={{
            toolbar: {setRows, setRowModesModel, addRecordMetaData},
          }}
          hideFooter={true}
        />
      </StyledBox>
    </>
  );
}
